/**
 * What the current user's project grants let the chat screen show.
 *
 * One hook rather than eight scattered `useHasProjectPermission` calls, because
 * the simplified profile is a *shape* — a PM sees a conversation and a preview,
 * and nothing that assumes they read diffs — and a shape assembled from eight
 * call sites drifts the first time someone adds a ninth panel.
 *
 * ── These are affordances, not a boundary ────────────────────────────────
 *
 * Every field here decides whether to render something. The server decides
 * whether the underlying thing is allowed, and for three of these it genuinely
 * does: `p.diff.view` closes the diff queries, `p.files.view` closes the three
 * workspace-file queries, `p.model.select` makes the server ignore a selection
 * the client sent anyway, and `p.env.use` gates creating a thread inside
 * somebody's environment. The other two — plan and branch — ride
 * inside the read model the thread already needs, so hiding them simplifies a
 * screen and keeps no secret. `packages/contracts/src/auth.ts` says the same
 * thing next to the permission list; it is worth saying twice.
 *
 * @module projectUiCapabilities
 */
import { useMemo } from "react";

import type { ProjectId } from "@taskin-code/contracts";

import { useHasProjectPermission } from "./authStore";

export interface ProjectUiCapabilities {
  /** Diff panel and its toggle. Backed by a real gate on the diff queries. */
  readonly canViewDiff: boolean;
  /**
   * Files panel, its toggle, and the composer's `@` file picker. Backed by a
   * real gate: without it the server refuses to list, search or read anything
   * in the workspace, so the panel is empty rather than merely hidden.
   */
  readonly canViewFiles: boolean;
  /** Plan sidebar and the button that opens it. UI only. */
  readonly canViewPlan: boolean;
  /** Provider/model/agent picker. The server pins the selection without it. */
  readonly canSelectModel: boolean;
  /** Branch toolbar. UI only. */
  readonly canViewBranch: boolean;
  /** Terminal panel and its toggle. */
  readonly canUseTerminal: boolean;
  /** File editor: opening the files panel to write. */
  readonly canEditFiles: boolean;
  /** Git actions control: commit, push, open a PR. */
  readonly canWriteGit: boolean;
  /** Starting and stopping the preview. */
  readonly canExposePreview: boolean;
  /** Opening threads inside a persistent environment. */
  readonly canUseEnvironment: boolean;
  /**
   * The Actions control: the run button and the list of the project's actions.
   * A real gate on background runs; for `terminal` and `chat` actions the gate
   * underneath is `p.terminal` and the ordinary turn.
   */
  readonly canRunActions: boolean;
  /**
   * The preview's "Public access" toggle. A real gate: `p.runtime.expose_public`
   * is re-checked on the server before any preview is published.
   */
  readonly canPublishPreview: boolean;
  /** "Add action" and the per-action edit gear. Backed by a real gate. */
  readonly canCreateActions: boolean;
  /**
   * The "Hand off to …" button, which continues the conversation in the other
   * provider. Backed by a real gate on `thread.handoff.create`.
   */
  readonly canHandOffThread: boolean;
  /**
   * The Pull Requests section for this project, and the pull request picker in the New thread
   * dialog. A real gate: without it the server leaves the project out of every listing.
   */
  readonly canViewPullRequests: boolean;
  /** Opening a thread here — the "Crear hilo" button on a pull request. Backed by a real gate. */
  readonly canCreateThread: boolean;
}

/**
 * Each permission is read as its own primitive selector and the object is
 * memoised: a selector that built the object inline would return a new
 * reference on every store change and re-render the whole chat view for a
 * keystroke somewhere else.
 */
export const useProjectUiCapabilities = (
  projectId: ProjectId | null | undefined,
): ProjectUiCapabilities => {
  const canViewDiff = useHasProjectPermission(projectId, "p.diff.view");
  const canViewFiles = useHasProjectPermission(projectId, "p.files.view");
  const canViewPlan = useHasProjectPermission(projectId, "p.plan.view");
  const canSelectModel = useHasProjectPermission(projectId, "p.model.select");
  const canViewBranch = useHasProjectPermission(projectId, "p.branch.view");
  const canUseTerminal = useHasProjectPermission(projectId, "p.terminal");
  const canEditFiles = useHasProjectPermission(projectId, "p.edit");
  const canWriteGit = useHasProjectPermission(projectId, "p.git_write");
  const canExposePreview = useHasProjectPermission(projectId, "p.runtime.expose");
  const canPublishPreview = useHasProjectPermission(projectId, "p.runtime.expose_public");
  const canUseEnvironment = useHasProjectPermission(projectId, "p.env.use");
  const canHandOffThread = useHasProjectPermission(projectId, "p.thread.handoff");
  const canRunActions = useHasProjectPermission(projectId, "p.action.run");
  const canCreateActions = useHasProjectPermission(projectId, "p.action.create");
  const canViewPullRequests = useHasProjectPermission(projectId, "p.pull_requests.view");
  const canCreateThread = useHasProjectPermission(projectId, "p.thread.create");

  return useMemo(
    () => ({
      canViewDiff,
      canViewFiles,
      canViewPlan,
      canSelectModel,
      canViewBranch,
      canUseTerminal,
      canEditFiles,
      canWriteGit,
      canExposePreview,
      canPublishPreview,
      canUseEnvironment,
      canHandOffThread,
      canRunActions,
      canCreateActions,
      canViewPullRequests,
      canCreateThread,
    }),
    [
      canViewDiff,
      canViewFiles,
      canViewPlan,
      canSelectModel,
      canViewBranch,
      canUseTerminal,
      canEditFiles,
      canWriteGit,
      canExposePreview,
      canPublishPreview,
      canUseEnvironment,
      canHandOffThread,
      canRunActions,
      canCreateActions,
      canViewPullRequests,
      canCreateThread,
    ],
  );
};
